import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, ArrowRight, Copy, Check, MessageCircle, FileText, Video, Lock, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChallengeSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, processing, success, error
  const [statusMessage, setStatusMessage] = useState('Verificando tu pago...');
  const [userData, setUserData] = useState(null);
  const [challengeData, setChallengeData] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [telegramLink, setTelegramLink] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 5;
  const retryDelay = 3000; // 3 segundos entre intentos
  const isProcessingRef = useRef(false);

  useEffect(() => {
    const reference = searchParams.get('reference');
    const sessionId = searchParams.get('session_id');
    const challengeId = searchParams.get('challenge_id');
    const isFree = searchParams.get('free') === 'true';

    if (isFree && challengeId) {
      handleFreeEnrollment(challengeId);
    } else if (reference || sessionId) {
      // Iniciar proceso de verificación con reintentos
      if (!isProcessingRef.current) {
        isProcessingRef.current = true;
        verifyAndCompleteEnrollment(reference || sessionId, challengeId, 0);
      }
    } else {
      setStatus('error');
      setStatusMessage('No se encontró información del pago');
    }
  }, [searchParams]);

  const handleFreeEnrollment = async (challengeId) => {
    setStatus('processing');
    setStatusMessage('Procesando inscripción gratuita...');
    
    try {
      const challengeRes = await axios.get(`${BACKEND_URL}/api/challenges`);
      const challenge = challengeRes.data.find(c => c.challenge_id === challengeId);
      if (challenge) {
        setChallengeData(challenge);
        setTelegramLink(challenge.telegram_link);
      }
      setStatus('success');
      toast.success('¡Inscripción completada!');
    } catch (error) {
      console.error('Error en inscripción gratuita:', error);
      setStatus('success');
    }
  };

  const verifyAndCompleteEnrollment = async (referenceOrSessionId, challengeId, attempt) => {
    // Actualizar mensaje según el intento
    if (attempt === 0) {
      setStatus('verifying');
      setStatusMessage('Verificando tu pago con el banco...');
    } else if (attempt === 1) {
      setStatusMessage('Confirmando la transacción...');
    } else if (attempt === 2) {
      setStatus('processing');
      setStatusMessage('Procesando tu inscripción...');
    } else {
      setStatusMessage(`Verificando pago... (intento ${attempt + 1}/${maxRetries})`);
    }

    try {
      // Obtener token si existe
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post(
        `${BACKEND_URL}/api/challenges/complete-enrollment`,
        {
          reference: referenceOrSessionId,
          session_id: referenceOrSessionId,
          challenge_id: challengeId
        },
        { headers }
      );

      // ¡ÉXITO!
      localStorage.setItem('session_token', response.data.session_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      setUserData(response.data.user);
      setTempPassword(response.data.temp_password);
      setTelegramLink(response.data.telegram_link);

      // Obtener datos del challenge
      if (response.data.challenge_id) {
        try {
          const challengeRes = await axios.get(`${BACKEND_URL}/api/challenges`);
          const challenge = challengeRes.data.find(c => c.challenge_id === response.data.challenge_id);
          if (challenge) {
            setChallengeData(challenge);
            if (!response.data.telegram_link && challenge.telegram_link) {
              setTelegramLink(challenge.telegram_link);
            }
          }
        } catch (e) {
          console.error('Error obteniendo challenge:', e);
        }
      }

      setStatus('success');
      setStatusMessage('¡Pago verificado exitosamente!');
      toast.success('¡Inscripción completada exitosamente!');

    } catch (error) {
      console.error(`Intento ${attempt + 1} falló:`, error.response?.data?.detail || error.message);
      
      // Si el error indica que debemos esperar, reintentar
      const errorDetail = error.response?.data?.detail || '';
      const shouldRetry = 
        errorDetail.includes('espera') || 
        errorDetail.includes('procesando') ||
        errorDetail.includes('pendiente') ||
        errorDetail.includes('No encontramos') ||
        error.response?.status === 404 ||
        error.response?.status === 400;

      if (shouldRetry && attempt < maxRetries - 1) {
        setRetryCount(attempt + 1);
        // Esperar y reintentar
        setTimeout(() => {
          verifyAndCompleteEnrollment(referenceOrSessionId, challengeId, attempt + 1);
        }, retryDelay);
      } else {
        // Después de todos los intentos, mostrar error pero con opción de reintentar
        setStatus('error');
        setStatusMessage(
          errorDetail || 
          'No pudimos verificar tu pago automáticamente. Si completaste el pago, haz clic en "Reintentar" o contacta soporte.'
        );
      }
    }
  };

  const handleManualRetry = () => {
    const reference = searchParams.get('reference');
    const sessionId = searchParams.get('session_id');
    const challengeId = searchParams.get('challenge_id');
    
    setRetryCount(0);
    isProcessingRef.current = false;
    verifyAndCompleteEnrollment(reference || sessionId, challengeId, 0);
  };

  const copyPassword = () => {
    if (tempPassword) {
      navigator.clipboard.writeText(tempPassword);
      setPasswordCopied(true);
      toast.success('Contraseña copiada');
      setTimeout(() => setPasswordCopied(false), 3000);
    }
  };

  // Estado: Verificando/Procesando
  if (status === 'verifying' || status === 'processing') {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8">
            {/* Animación de carga */}
            <div className="relative mx-auto w-24 h-24 mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-zinc-700"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-pink-500 animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-pink-500 animate-pulse" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-3">
              {status === 'verifying' ? 'Verificando tu pago' : 'Procesando inscripción'}
            </h2>
            
            <p className="text-zinc-400 mb-4">
              {statusMessage}
            </p>
            
            {retryCount > 0 && (
              <div className="bg-zinc-800/50 rounded-xl p-3 mb-4">
                <p className="text-sm text-zinc-500">
                  Esto puede tomar unos segundos mientras confirmamos con el banco...
                </p>
              </div>
            )}
            
            <div className="flex justify-center gap-2">
              {[...Array(5)].map((_, i) => (
                <div 
                  key={i} 
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    i <= retryCount ? 'bg-pink-500' : 'bg-zinc-700'
                  }`}
                />
              ))}
            </div>
            
            <p className="text-xs text-zinc-600 mt-4">
              No cierres esta página
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Estado: Error
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8">
            <div className="w-20 h-20 mx-auto bg-yellow-500/20 rounded-full flex items-center justify-center mb-6">
              <AlertCircle className="w-10 h-10 text-yellow-500" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-3">
              Verificación pendiente
            </h2>
            
            <p className="text-zinc-400 mb-6">
              {statusMessage}
            </p>
            
            <div className="space-y-3">
              <Button 
                onClick={handleManualRetry}
                className="w-full bg-pink-600 hover:bg-pink-500 text-white rounded-full py-6"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Reintentar verificación
              </Button>
              
              <Button 
                onClick={() => navigate('/challenges')}
                variant="outline"
                className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-full py-6"
              >
                Volver a Challenges
              </Button>
            </div>
            
            <p className="text-xs text-zinc-500 mt-6">
              Si el problema persiste y tu pago fue procesado, contáctanos por WhatsApp
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Estado: Éxito
  return (
    <div className="min-h-screen bg-[#09090b] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header de éxito */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8 text-center mb-6">
          <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-6">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-2" style={{fontFamily: 'Syne, sans-serif'}}>
            ¡Inscripción Exitosa!
          </h1>
          
          <p className="text-zinc-400">
            Ya tienes acceso al challenge {challengeData?.title && `"${challengeData.title}"`}
          </p>
        </div>

        {/* Contraseña temporal (solo para nuevos usuarios) */}
        {tempPassword && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <Lock className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-bold text-yellow-400 mb-2">Tu contraseña temporal</h3>
                <p className="text-sm text-zinc-400 mb-3">
                  Guarda esta contraseña para acceder a tu cuenta. Te recomendamos cambiarla después.
                </p>
                <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-3">
                  <code className="flex-1 text-white font-mono">{tempPassword}</code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={copyPassword}
                    className="text-zinc-400 hover:text-white"
                  >
                    {passwordCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Link de Telegram */}
        {telegramLink && (
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-white mb-1">Únete a la comunidad</h3>
                <p className="text-blue-200 text-sm mb-3">
                  Conecta con otras participantes del challenge
                </p>
                <a
                  href={telegramLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-blue-600 px-5 py-2 rounded-full font-bold hover:bg-blue-50 transition-colors"
                >
                  Unirme al grupo
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Materiales del challenge */}
        {challengeData?.materials && challengeData.materials.length > 0 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 mb-6">
            <h3 className="font-bold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-pink-500" />
              Materiales del Challenge
            </h3>
            <div className="space-y-3">
              {challengeData.materials.map((material, index) => (
                <a
                  key={index}
                  href={material.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-zinc-800/50 hover:bg-zinc-800 rounded-xl transition-colors group"
                >
                  {material.type === 'video' ? (
                    <Video className="w-5 h-5 text-blue-400" />
                  ) : (
                    <FileText className="w-5 h-5 text-red-400" />
                  )}
                  <span className="flex-1 text-zinc-300 group-hover:text-white transition-colors">
                    {material.name}
                  </span>
                  <ArrowRight className="w-4 h-4 text-zinc-500 group-hover:text-pink-500 transition-colors" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Botón para ir al challenge */}
        <div className="text-center space-y-4">
          <Button
            onClick={() => navigate(`/challenge/${challengeData?.challenge_id || searchParams.get('challenge_id')}`)}
            className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-8 py-6 font-bold"
          >
            Ver mi Challenge
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <p className="text-sm text-zinc-500">
            También puedes acceder desde tu Dashboard
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChallengeSuccess;
